import { computed, ref, reactive, type Reactive } from 'vue';
import { toValue, provideLocal, injectLocal } from '@vueuse/core';
import { Cookie } from '@vue-util/cookie';
import { useProduct } from '~/modules/product/composables/useProduct';
import { getBigTicketZipCookie } from '~/utils/cookiesUtils';
import { get } from '~/utils/api';
import useFetch from './api/useFetch';
import { usePageData } from './usePageData';
import { useUrlLocationOperations } from './useUrlLocationOperations';
import type { BopsData } from '~/declaration/bops';

export const BOPS_DATA_REPOSITORY: InjectionKey<Record<ProductId, BopsData>> = Symbol.for('BOPS_DATA_REPOSITORY');

/**
 * @typedef BopsDataOptions
 * @property {ProductId} productId
 * @property {string} shoppingMode
 * @property {string} regionCode
 * @property {string} currencyCode
 * @property {string} customerState
 * @property {string} deviceType
 * @property {string} radius
 * @property {import('@vueuse/core').MaybeRefOrGetter<string>} zipCode
 * @property {string} filterType
 *
 * @description
 * This composable is used to get BOPS data for a product.
 * Depends on the useProduct, usePageData provided states.
 */

export const useBopsDataProvide = () => {
  const bopsDataState: Reactive<Record<ProductId, BopsData>> = reactive({});
  provideLocal(BOPS_DATA_REPOSITORY, bopsDataState);
};

/**
 *
 * @param {Partial<BopsDataOptions>} options
 */

export default function useBopsData({ shoppingMode, regionCode, currencyCode, customerState, deviceType, radius, zipCode, filterType } = {}) {
  const { productId } = useProduct();
  const { getSearchParamsData } = useUrlLocationOperations();

  /**
   * @type {ProductId}
   */
  let pageProductId;
  let pageShoppingMode;
  let pageRegionCode;
  let pageCurrencyCode;

  try {
    const pageData = usePageData();
    pageProductId = pageData.productId;
    pageShoppingMode = pageData.shoppingMode;
    pageRegionCode = pageData.regionCode;
    pageCurrencyCode = pageData.currencyCode;
  } catch {
    pageProductId = getSearchParamsData()?.get('ID');
  }

  const pid = productId || pageProductId;
  const sMode = shoppingMode || pageShoppingMode || 'SITE';
  const rCode = regionCode || pageRegionCode || 'US';
  const cCode = currencyCode || pageCurrencyCode || 'USD';
  const cState = customerState || 'GUEST';
  const dType = deviceType || 'DESKTOP';
  const rad = radius || 'M100';
  const zCode = computed(() => toValue(zipCode) || getBigTicketZipCookie() || Cookie.getUserZipCode());
  const fType = filterType || 'storeavailable';

  const bopsDataStore = injectLocal(BOPS_DATA_REPOSITORY);
  if (!bopsDataStore) {
    console.error('useBopsData() called without provider.');
    return;
  }
  const bopsData = (bopsDataStore[pid] = bopsDataStore[pid] || ref(null));

  /**
   * @type {import('./api/useFetch').FetchApi<BopsData>}
   */
  const api = useFetch(
    () =>
      get(
        `/xapi/digital/v1/product/${pid}/store/bopsavailablebylocation?_shoppingMode=${sMode}&_regionCode=${rCode}&currencyCode=${cCode}&_customerState=${cState}&_deviceType=${dType}&radius=${rad}&searchAddress=${zCode.value}&filtertype=${fType}`,
      ),
    {
      initialValue: bopsData,
    },
  );

  const nearestStore = computed(() => api.result.value?.stores?.available[0] || null);

  return {
    ...api,
    nearestStore,
  };
}
